<i18n
  src="@/locales/views/agro_exporter/production/basic_container_quality/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container_final_quality.yml" lang="yaml"></i18n>

<template>
  <div class="mt-6">
    <h1 class="page-title">
      <i class="fa-solid fa-chart-column"></i>
      <span class="space-icon"></span>{{ $t("labels.graphics_title") }}
    </h1>

    <div class="flex flex-col mt-4">
      <div class="panel">
        <div class="panel-body" id="chart">
          <apexchart
            type="bar"
            width="100%"
            height="380"
            :options="weekChartOptions"
            :series="weekSeries"
          ></apexchart>
        </div>
      </div>

      <div class="panel">
        <div class="panel-body" id="chart">
          <apexchart
            type="bar"
            width="100%"
            height="200"
            :options="summaryChartOptions"
            :series="summarySeries"
          ></apexchart>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";

export default {
  name: "AEProductionBasicContainerExportShowProcessGraphics",
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {};
  },
  props: {
    agro_exporter_process: { required: true },
    containers: {
      type: Array,
      required: true
    }
  },
  computed: {
    containers_reverse() {
      return [...this.containers].reverse();
    },
    // This method returns an array of the weeks of the containers only once order by desc
    weeks: function () {
      return this.containers_reverse
        .map(container => container.week)
        .filter(week => week != null && week != undefined)
        .filter((value, index, self) => self.indexOf(value) === index);
    },
    containers_by_week: function () {
      return this.weeks.map(week => {
        return this.containers_reverse
          .filter(container => container.week == week)
          .map(container => {
            let status_id = container.container_final_quality?.status_id;
            if (status_id != undefined) status_id += 1;
            else status_id = 0;
            return {
              week: container.week,
              container_code: container.container_code,
              status_id: status_id
            };
          });
      });
    },
    weekChartOptions() {
      return {
        chart: { type: "bar" },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "50%",
            endingShape: "rounded",
            colors: {
              ranges: [
                { from: 1, to: 1, color: "#dc2626" },
                { from: 2, to: 2, color: "#eab308" },
                { from: 3, to: 3, color: "#65a30d" }
              ]
            }
          }
        },
        dataLabels: {
          enabled: false,
          orientation: "vertical"
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        xaxis: {
          categories: this.weeks.map(week => week),
          title: {
            text: "Semana",
            style: { fontSize: "14px" },
            offsetY: 100
          }
        },
        yaxis: {
          max: 3,
          min: 0,
          title: {
            text: "Calidad",
            style: { fontSize: "14px" },
            offsetX: 0
          },
          tickAmount: 3,
          labels: {
            formatter: function (val) {
              if (val == 1)
                return this.$t(
                  "model.container_final_quality.statuses.rejected"
                );
              else if (val == 2)
                return this.$t(
                  "model.container_final_quality.statuses.medium_quality"
                ).split(" ");
              else if (val == 3)
                return this.$t(
                  "model.container_final_quality.statuses.high_quality"
                ).split(" ");
            }.bind(this),
            style: { fontSize: "14px" },
            offsetX: 20
          }
        },
        fill: { opacity: 1 },
        legend: { show: false },
        tooltip: {
          enabled: true,
          x: {
            show: false
          },
          y: {
            formatter: function (_, { dataPointIndex }) {
              return this.containers_reverse[dataPointIndex].container_code;
            }.bind(this),
            title: {
              formatter: () => {
                return "";
              }
            }
          }
        }
      };
    },

    /*
      This method returns an array of objects with the data to be displayed in the chart.
      The data is an array of numbers, where each number represents the status of a container.
      The status is represented by a number, where:
        0: Rechazado
        1: Calidad media
        2: Calidad alta
      */
    weekSeries() {
      const len = Math.max(...this.containers_by_week.map(arr => arr?.length));
      let series = [];
      for (let i = 0; i < len; i++) {
        series.push({
          data: this.containers_by_week.map(arr => arr[i]?.status_id)
        });
      }
      return series;
    },

    summaryChartOptions() {
      return {
        chart: {
          type: "bar",
          stacked: true
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              total: {
                enabled: false,
                style: {
                  fontSize: "14px",
                  fontWeight: 900
                }
              }
            },
            barHeight: "30%"
          }
        },
        stroke: {
          width: 1,
          colors: ["#fff"]
        },
        yaxis: {
          labels: { show: false }
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          offsetX: 40,
          fontSize: "14px"
        },
        dataLabels: {
          formatter: function (val) {
            let total = this.containers.length;
            return `${val} (${((val * 100) / total).toFixed(0)}%)`;
          }.bind(this),
          style: {
            fontSize: "14px"
          }
        },
        colors: ["#dc2626", "#eab308", "#65a30d"]
      };
    },
    summarySeries() {
      return [
        {
          name: this.$t("model.container_final_quality.statuses.rejected"),
          data: [
            this.containers.filter(
              container => container.container_final_quality?.status_id == 0
            ).length
          ]
        },
        {
          name: this.$t(
            "model.container_final_quality.statuses.medium_quality"
          ),
          data: [
            this.containers.filter(
              container => container.container_final_quality?.status_id == 1
            ).length
          ]
        },
        {
          name: this.$t("model.container_final_quality.statuses.high_quality"),
          data: [
            this.containers.filter(
              container => container.container_final_quality?.status_id == 2
            ).length
          ]
        }
      ];
    }
  }
};
</script>
